import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2, XCircle, Clock, AlertTriangle, TrendingUp,
  Briefcase, Activity, Bell, ChevronRight, Zap, Timer, Loader2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [toggling, setToggling] = useState(false);
  const [timesheet, setTimesheet] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [shiftElapsed, setShiftElapsed] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [vendorRes, statsRes, notifsRes, actRes, tsRes] = await Promise.all([
        API.get("/technicians/me"),
        API.get("/technicians/me/stats"),
        API.get("/technicians/me/notifications?limit=5"),
        API.get("/technicians/me/activity?limit=5"),
        API.get("/technicians/me/timesheet"),
      ]);
      setVendor(vendorRes.data);
      setStats(statsRes.data);
      setNotifications(notifsRes.data.notifications);
      setActivities(actRes.data.activities);
      setTimesheet(tsRes.data.timesheet);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);

  // Shift timer
  useEffect(() => {
    if (!timesheet || !timesheet.clock_in || timesheet.clock_out) return;
    const onBreak = timesheet.breaks?.length > 0 && !timesheet.breaks[timesheet.breaks.length - 1].end;
    const update = () => {
      const diff = Math.max(0, Math.floor((Date.now() - new Date(timesheet.clock_in).getTime()) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setShiftElapsed(`${h}h ${m}m ${s}s`);
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [timesheet]);

  const handleClockIn = async () => {
    setClockLoading(true);
    try {
      await API.post("/technicians/me/clock-in");
      toast.success("Clocked in! Shift started.");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setClockLoading(false); }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      const { data } = await API.post("/technicians/me/clock-out");
      toast.success(`Clocked out! Total: ${data.total_hours}h`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setClockLoading(false); }
  };

  const handleBreak = async () => {
    setClockLoading(true);
    try {
      const { data } = await API.post("/technicians/me/break");
      toast.success(data.on_break ? "Break started" : "Break ended");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setClockLoading(false); }
  };

  const isClockedIn = timesheet && timesheet.clock_in && !timesheet.clock_out;
  const isOnBreak = isClockedIn && timesheet.breaks?.length > 0 && !timesheet.breaks[timesheet.breaks.length - 1].end;

  const toggleStatus = async () => {
    if (!vendor) return;
    setToggling(true);
    try {
      const newStatus = vendor.is_online ? "offline" : "online";
      await API.put("/technicians/me/status", { status: newStatus });
      setVendor(prev => ({ ...prev, is_online: !prev.is_online, current_status: newStatus }));
      toast.success(`You are now ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setToggling(false);
    }
  };

  if (!vendor || !stats) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const approvalColor = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    on_hold: "bg-rose-50 text-rose-700 border-rose-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    under_review: "bg-blue-50 text-blue-700 border-blue-200",
  };

  return (
    <div className="space-y-4" data-testid="vendor-dashboard">
      {/* Status Card */}
      <Card className="border border-zinc-200 overflow-hidden animate-in" data-testid="status-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-heading font-bold text-zinc-900">{vendor.full_name || "Welcome"}</h2>
              <p className="text-xs text-zinc-500">{vendor.company_name || "Set up your profile"}</p>
            </div>
            <div className={`text-xs font-medium px-2.5 py-1 rounded-full border ${approvalColor[vendor.approval_status] || "bg-zinc-50 text-zinc-600"}`} data-testid="approval-status">
              {vendor.approval_status}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className={`status-dot ${vendor.is_online ? "online" : "offline"}`} />
              <div>
                <p className="text-sm font-semibold text-zinc-900">
                  {vendor.is_online ? "Online" : "Offline"}
                </p>
                <p className="text-[10px] text-zinc-400">
                  {vendor.is_online ? "Receiving requests" : "Not receiving requests"}
                </p>
              </div>
            </div>
            <Switch
              checked={vendor.is_online}
              onCheckedChange={toggleStatus}
              disabled={toggling || vendor.approval_status !== "approved"}
              data-testid="online-toggle"
            />
          </div>
          {vendor.approval_status !== "approved" && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Account must be approved to go online
            </p>
          )}
        </CardContent>
      </Card>

      {/* Clock In/Out Card */}
      <Card className={`border overflow-hidden animate-in ${isClockedIn ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200"}`} data-testid="clock-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-500" />
              <span className="text-sm font-heading font-semibold text-zinc-900">
                {isClockedIn ? "On Shift" : "Off Shift"}
              </span>
            </div>
            {isClockedIn && (
              <span className="text-sm font-mono font-bold text-emerald-700" data-testid="shift-timer">
                {shiftElapsed}
              </span>
            )}
          </div>

          {isClockedIn ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span>Clocked in: {new Date(timesheet.clock_in).toLocaleTimeString()}</span>
                {isOnBreak && <Badge className="bg-amber-100 text-amber-700 text-[9px]">On Break</Badge>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleBreak} disabled={clockLoading}
                  className={`flex-1 text-xs h-9 ${isOnBreak ? "border-amber-300 text-amber-700" : ""}`}
                  data-testid="break-btn">
                  {isOnBreak ? "End Break" : "Start Break"}
                </Button>
                <Button size="sm" onClick={handleClockOut} disabled={clockLoading}
                  className="flex-1 text-xs h-9 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="clock-out-btn">
                  {clockLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Clock Out"}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={handleClockIn} disabled={clockLoading}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
              data-testid="clock-in-btn">
              {clockLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Clock className="w-4 h-4 mr-2" /> Clock In — Start Shift</>}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 animate-in animate-delay-1">
        <Card className="border border-zinc-200 card-hover cursor-pointer" onClick={() => navigate("/vendor/active")} data-testid="active-jobs-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-4 h-4 text-zinc-400" />
              <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">Active Jobs</span>
            </div>
            <p className="text-2xl font-heading font-bold text-zinc-900">{stats.active_jobs}</p>
          </CardContent>
        </Card>
        <Card className="border border-red-200 bg-red-50/30 card-hover cursor-pointer" onClick={() => navigate("/vendor/requests")} data-testid="today-requests-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-red-500" />
              <span className="text-[10px] text-red-500 uppercase tracking-wider font-bold">Today</span>
            </div>
            <p className="text-2xl font-heading font-bold text-zinc-900">{stats.today?.total || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <Card className="border border-zinc-200 animate-in animate-delay-2" data-testid="performance-card">
        <CardContent className="p-5">
          <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Performance</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-zinc-600">Acceptance Rate</span>
              </div>
              <span className="text-sm font-bold text-zinc-900" data-testid="acceptance-rate">{stats.acceptance_rate}%</span>
            </div>
            <Progress value={stats.acceptance_rate} className="h-1.5" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-zinc-600">Completion Rate</span>
              </div>
              <span className="text-sm font-bold text-zinc-900" data-testid="completion-rate">{stats.completion_rate}%</span>
            </div>
            <Progress value={stats.completion_rate} className="h-1.5" />

            <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-600">Avg Response</span>
              </div>
              <span className="text-sm font-bold text-zinc-900" data-testid="avg-response-time">
                {stats.avg_response_time_seconds > 0 ? `${Math.round(stats.avg_response_time_seconds)}s` : "N/A"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Today Summary */}
      <div className="grid grid-cols-3 gap-2 animate-in animate-delay-2">
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
          <p className="text-lg font-heading font-bold text-emerald-700">{stats.today?.accepted || 0}</p>
          <p className="text-[10px] text-emerald-600 font-medium">Accepted</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-lg font-heading font-bold text-amber-700">{stats.today?.passed || 0}</p>
          <p className="text-[10px] text-amber-600 font-medium">Passed</p>
        </div>
        <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-100">
          <p className="text-lg font-heading font-bold text-rose-700">{stats.today?.missed || 0}</p>
          <p className="text-[10px] text-rose-600 font-medium">Missed</p>
        </div>
      </div>

      {/* Profile Completion */}
      <Card className="border border-zinc-200 animate-in animate-delay-3" data-testid="profile-completion-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-zinc-700">Profile Completion</span>
            <span className="text-sm font-bold text-zinc-900">{stats.profile_completion}%</span>
          </div>
          <Progress value={stats.profile_completion} className="h-2" />
          {stats.profile_completion < 100 && (
            <Button variant="ghost" size="sm" className="mt-2 text-xs text-zinc-500 p-0 h-auto"
              onClick={() => navigate("/vendor/profile")} data-testid="complete-profile-btn">
              Complete your profile <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Recent Notifications */}
      <Card className="border border-zinc-200 animate-in animate-delay-3" data-testid="recent-notifications">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Notifications</h3>
            <Button variant="ghost" size="sm" className="text-xs h-auto p-0 text-zinc-500"
              onClick={() => navigate("/vendor/notifications")} data-testid="view-all-notifications">
              View All <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          {notifications.length === 0 ? (
            <p className="text-xs text-zinc-400 text-center py-4">No recent notifications</p>
          ) : (
            <div className="space-y-2">
              {notifications.slice(0, 3).map((n) => (
                <div key={n.id} className={`flex items-start gap-3 p-2.5 rounded-lg ${!n.is_read ? "bg-rose-50/50" : "bg-zinc-50"}`}>
                  <Bell className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${!n.is_read ? "text-rose-500" : "text-zinc-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-zinc-800 truncate">{n.title}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{n.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lifetime Stats */}
      <Card className="border border-zinc-200 animate-in animate-delay-4" data-testid="lifetime-stats">
        <CardContent className="p-4">
          <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-3">Lifetime Stats</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-2 bg-zinc-50 rounded-lg">
              <p className="text-lg font-heading font-bold text-zinc-900">{stats.lifetime?.total || 0}</p>
              <p className="text-[10px] text-zinc-500">Total Offers</p>
            </div>
            <div className="text-center p-2 bg-zinc-50 rounded-lg">
              <p className="text-lg font-heading font-bold text-zinc-900">{stats.lifetime?.completed || 0}</p>
              <p className="text-[10px] text-zinc-500">Completed</p>
            </div>
            <div className="text-center p-2 bg-zinc-50 rounded-lg">
              <p className="text-lg font-heading font-bold text-zinc-900">{stats.rating_average || "N/A"}</p>
              <p className="text-[10px] text-zinc-500">Rating</p>
            </div>
            <div className="text-center p-2 bg-zinc-50 rounded-lg">
              <p className="text-lg font-heading font-bold text-zinc-900">{stats.rating_count}</p>
              <p className="text-[10px] text-zinc-500">Reviews</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
